import React, { useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, notification } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import logo from "../../../Assets/Images/logo.svg";
import secured from "../../../Assets/Icons/secure.svg";
import { verifyTwoFactor, resendTwoFactor } from "../../../API/auth";
import { UserContext } from "../../../App";
import updateUserStates, {
  setStoredToken,
  setStoredUser,
} from "../../../Utlis/UpdateUserState";
import getPortalByTenantType from "../../../Utlis/GetPortalByTenantType";
import getFirstAllowedRoute from "../../../Utlis/GetFirstAllowedRoute";
import PortalConfig from "../../../Utlis/PortalConfig";
import "./style.css";

const TwoFactorAuthentication = ({ setUser, portal }) => {
  const config = PortalConfig[portal];
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const loginTenantType =
    location.state?.tenantType || config.loginTenantTypes[0];
  const { setUserData, setUserToken } = useContext(UserContext);

  const [loading, setLoading] = React.useState(false);
  const inputs = useRef([]);
  const [timeLeft, setTimeLeft] = React.useState(59);
  const [tempToken, setTempToken] = React.useState(location.state?.tempToken);
  const [roleError, setRoleError] = React.useState("");

  React.useEffect(() => {
    if (!tempToken || !email) {
      navigate(config.loginRoute, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearInputs = () => {
    inputs.current.forEach((el) => el && (el.value = ""));
    inputs.current[0]?.focus();
  };

  const handleBack = () => {
    setUser(config.userValue);
    navigate("/selection");
  };

  const isRevisionRequested = (res, tenantType) =>
    Boolean(config.reviseRoute) &&
    res.data.status === "revision_requested" &&
    tenantType === config.reviseTenantType;

  const goToRevise = (res) => {
    notification.info({
      message: "Revision Requested",
      description:
        res?.data?.message ||
        "Your application needs revision before it can be approved.",
      placement: "topRight",
    });
    updateUserStates(setUserData, setUserToken);
    navigate(config.reviseRoute);
    notification.success({
      message: "Login successful",
      placement: "topRight",
    });
  };

  const handleVerify = async () => {
    const code = inputs.current.map((el) => el?.value || "").join("");

    setLoading(true);
    const res = await verifyTwoFactor({ temp_token: tempToken, code });
    setLoading(false);

    if (res?.status === 200 && res?.data?.access) {
      const tenantType = res.data.tenant_type || loginTenantType;

      if (!config.expectedTenantTypes.includes(tenantType)) {
        setRoleError(
          `This account isn't registered as ${config.roleLabel}. Please use the correct login screen for your role.`,
        );
        clearInputs();
        return;
      }

      setRoleError("");
      localStorage.clear();
      setStoredToken({
        access: res.data.access,
        refresh: res.data.refresh,
      });
      setStoredUser({ ...res.data, tenant_type: tenantType });

      let portalInfo;

      if (config.reviseChecksBeforePortal) {
        if (isRevisionRequested(res, tenantType)) {
          goToRevise(res);
          return;
        }
        updateUserStates(setUserData, setUserToken);
        portalInfo = getPortalByTenantType(tenantType) || config.fallbackPortal;
        setUser(portalInfo.userType);
      } else {
        updateUserStates(setUserData, setUserToken);
        portalInfo = getPortalByTenantType(tenantType) || config.fallbackPortal;
        setUser(portalInfo.userType);

        if (isRevisionRequested(res, tenantType)) {
          goToRevise(res);
          return;
        }
      }

      notification.success({
        message: "Login successful",
        placement: "topRight",
      });
      const landingRoute = res.data.subscription_payment_required
        ? "/my-subscription"
        : getFirstAllowedRoute(
            portalInfo.userType,
            res.data.page_access,
            portalInfo.dashboardRoute,
          );
      setTimeout(() => navigate(landingRoute), 1000);
    } else {
      setRoleError("");
      notification.error({
        message: "Verification failed",
        description: res?.data?.detail || "Invalid or expired code.",
        placement: "topRight",
      });
      clearInputs();
    }
  };

  React.useEffect(() => {
    if (timeLeft === 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleResend = async () => {
    const res = await resendTwoFactor(tempToken);

    if (res?.status === 200) {
      if (res?.data?.temp_token) {
        setTempToken(res.data.temp_token);
      }
      setTimeLeft(59);
      clearInputs();
      notification.success({
        message: "Code Sent",
        description:
          res?.data?.detail ||
          "A new verification code has been sent to your email.",
        placement: "topRight",
      });
    } else {
      notification.error({
        message: "Could not resend code",
        description: res?.data?.detail || "Please try logging in again.",
        placement: "topRight",
      });
    }
  };

  const handleInput = (e, index) => {
    const value = e.target.value;
    if (value.length === 1 && index < inputs.current.length - 1) {
      inputs.current[index + 1].focus();
    }
    if (value.length === 0 && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    const emptyBox = inputs.current.find((el) => !el?.value);
    if (emptyBox) {
      emptyBox.focus();
      return;
    }
    handleVerify();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      e.preventDefault();
      const prev = inputs.current[index - 1];
      prev.value = "";
      prev.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < inputs.current.length - 1) {
      e.preventDefault();
      inputs.current[index + 1].focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, inputs.current.length - index);
    if (!digits) return;

    digits.split("").forEach((char, offset) => {
      const el = inputs.current[index + offset];
      if (el) el.value = char;
    });

    const nextIndex = Math.min(
      index + digits.length,
      inputs.current.length - 1,
    );
    inputs.current[nextIndex]?.focus();
  };
  return (
    <div className="factor">
      <div className="factor-page">
        <Button
          className="factor-back"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        />

        <div className="logo">
          <img src={logo} alt="logo" />
        </div>

        <div className="factor-card">
          <div className="top-gradient"></div>
          <div className="email-logo">
            <img src={secured} alt="secured" />
            <div className="text">Secure verification step</div>
          </div>

          <div className="content-heading">Two-Factor Authentication</div>
          <div className="content-wrapper">
            <div className="sub-heading">
              Please enter the 6-digit code sent to your email
              <span className="email"> {email}.</span>
            </div>
          </div>

          <form className="factor-form" onSubmit={handleSubmit}>
            <div className="two-factor-input">
              <div className="code-group">
                {[0, 1, 2].map((i) => (
                  <input
                    key={i}
                    type="text"
                    maxLength="1"
                    ref={(el) => (inputs.current[i] = el)}
                    onChange={(e) => handleInput(e, i)}
                    onPaste={(e) => handlePaste(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                  />
                ))}
              </div>

              <div className="dot-separator">•</div>

              <div className="code-group">
                {[3, 4, 5].map((i) => (
                  <input
                    key={i}
                    type="text"
                    maxLength="1"
                    ref={(el) => (inputs.current[i] = el)}
                    onChange={(e) => handleInput(e, i)}
                    onPaste={(e) => handlePaste(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                  />
                ))}
              </div>
            </div>

            {roleError && <div className="factor-role-error">{roleError}</div>}

            <div className="actions">
              <span className="timer">
                Code expires in{" "}
                <span className="time">{formatTime(timeLeft)}</span>
              </span>
              <span className="resend" onClick={handleResend}>
                Resend code
              </span>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              className="submit-button"
              loading={loading}
            >
              Verify & Continue
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthentication;
